import { useEffect, useState, useContext } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getUnmatchedDeclarations } from '../../../api/declarations';
import DataTable, { useDataTableHandle } from '../../DataTable';
import DeclarationsActionsButton from '../DeclarationsActionsButton/DeclarationsActionsButton';
import UnmatchedDeclarationsFilters from './UnmatchedDeclarationsFilters';
import ResetButton from '../../Buttons/ResetButton';
import { StoreContext } from '../../Store';

const UNMATCHED_STATUS_ID = '70';
const DEFAULT_FILTER_VALUES = { declarationStatus: UNMATCHED_STATUS_ID };

const UnmatchedDeclarations = () => {
  const navigate = useNavigate();
  const store = useContext(StoreContext);
  const [loadingDeclarations, setLoadingDeclarations] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState(DEFAULT_FILTER_VALUES);
  const [pagination, setPagination] = useState({ pageNo: 0, itemsPerPage: 10 });
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const dth = useDataTableHandle();

  const persistedFilters = store.unmatchedDeclarationsFilters[0]?.filters || {};
  const isDefaultFilters = (f) => {
    const pf = f || {};
    const onlyUnmatched =
      (pf.declarationStatus === UNMATCHED_STATUS_ID || pf.declarationStatus == null) &&
      !pf.LRN &&
      !pf.MRN &&
      !pf.createdDateFrom &&
      !pf.createdDateTo;
    return onlyUnmatched;
  };
  const hasSort = !!(dth?.current?.sortStatus?.currentKey && Object.keys(dth.current.sortStatus || {}).length);
  const hasFiltersApplied = !isDefaultFilters(persistedFilters) || hasSort;

  const buildFilterPayload = (filters) => {
    const f = filters ?? persistedFilters;
    const effectiveFilters = Object.keys(f || {}).length ? f : DEFAULT_FILTER_VALUES;
    const str = (v) => (v != null && v !== '' ? String(v) : null);
    return {
      LRN: str(effectiveFilters.LRN),
      MRN: str(effectiveFilters.MRN),
      declarationStatus: str(effectiveFilters.declarationStatus),
      createdDateFrom: str(effectiveFilters.createdDateFrom),
      createdDateTo: str(effectiveFilters.createdDateTo),
    };
  };

  const fetchData = async (newFilters, goToFirstPage = false, resetSort = false) => {
    setLoadingDeclarations(true);
    try {
      const pagination = dth.current.pagination;
      const sortStatus = resetSort ? {} : dth.current.sortStatus;
      const filtersToSend = buildFilterPayload(newFilters);
      const hasFilters = Object.values(filtersToSend).some((v) => v != null);

      const filter = hasFilters ? filtersToSend : {};

      const resp = await getUnmatchedDeclarations(
        filter,
        goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        pagination.itemsPerPage,
        sortStatus
      );

      dth.current.setServerDataLength(resp.totalItems);
      setDeclarations(resp.data);
      if (goToFirstPage) dth.current.resetPagination();
    } finally {
      setLoadingDeclarations(false);
    }
  };

  useEffect(() => {
    const hasPersisted = Object.keys(persistedFilters || {}).length > 0;
    if (!hasPersisted) {
      store.unmatchedDeclarationsFilters[1]({ filters: DEFAULT_FILTER_VALUES });
      setFilterData(DEFAULT_FILTER_VALUES);
    } else {
      setFilterData({ ...persistedFilters });
      if (hasFiltersApplied) setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (paginationLoaded) fetchData();
  }, [paginationLoaded]);

  const onUpdateFilters = async (setPaginationLength, pagination, sortStatus) => {
    const filtersToSend = buildFilterPayload();
    const hasFilters = Object.values(filtersToSend).some((v) => v != null);
    const filter = hasFilters ? filtersToSend : {};
    const response = await getUnmatchedDeclarations(
      filter,
      pagination.pageNo * pagination.itemsPerPage,
      pagination.itemsPerPage,
      sortStatus
    );
    setPaginationLength(response.totalItems);
    setDeclarations(response.data);
  };

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus?.({});
    store.unmatchedDeclarationsFilters[1]({ filters: DEFAULT_FILTER_VALUES });
    setFilterData(DEFAULT_FILTER_VALUES);
    await fetchData(DEFAULT_FILTER_VALUES, true, true);
  };

  const handleSubmitFilters = async () => {
    store.unmatchedDeclarationsFilters[1]({ filters: { ...filterData } });
    await fetchData(filterData, true);
  };

  return (
    <div className="full-width-container declarations-actions">
      <section className="floating-box position-relative p-3" style={{ maxWidth: '1040px' }}>
        <div className="d-flex justify-content-between align-items-center pb-2">
          <h1 className="page-heading">Unmatched Declarations</h1>
          <div className="d-flex gap-4 align-items-center flex-shrink-0">
            {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
            <button
              className="stepper-button"
              onClick={() => setShowFilters(!showFilters)}
              style={{ minWidth: 145, maxWidth: 145 }}
              disabled={hasFiltersApplied && showFilters}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        <UnmatchedDeclarationsFilters
          visible={showFilters}
          values={filterData}
          onChange={setFilterData}
          onSubmit={handleSubmitFilters}
        />

        <div className="pb-4 mb-4">
          <DataTable
            dontRefreshPagination
            idKey="id"
            data={declarations}
            className="table-striped"
            lazyLoading
            showAdvnSearch={false}
            isLoading={loadingDeclarations}
            persistGetter={pagination}
            persistSetter={setPagination}
            handle={dth}
            sortable
            filterable={false}
            onUpdate={onUpdateFilters}
            onPaginationLoaded={() => setPaginationLoaded(true)}
          >
            <DataTable.Pagination itemsPerPage={[10, 25, 50]} className="w-100 d-flex justify-content-end" />
            <DataTable.Row style={{ fontSize: '14px' }} />
            <DataTable.Column colKey="createdOn" header="Declarations Date" hideFilter />
            <DataTable.Column colKey="LRN" header="LRN" />
            <DataTable.Column colKey="MRN" header="MRN" />
            <DataTable.Column header="Actions">
              {(item) => {
                return <DeclarationsActionsButton id={item.id} />;
              }}
            </DataTable.Column>
          </DataTable>
        </div>
        <Button variant="secondary" className="mt-4 align-self-end input-inner-container" onClick={() => navigate(-1)}>
          Back
        </Button>
      </section>
    </div>
  );
};

export default UnmatchedDeclarations;
